/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useListBatchContactImports} from '@agent-assist/api-typescript-react-query-hooks';
import {Box, Stack} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {FC} from 'react';

import {toDate} from '../services/dates';

const PAGE_SIZE = 1000;

interface ImportsSelectProps {
  onSelect: (id: string) => void;
}

const ImportsSelect: FC<ImportsSelectProps> = ({onSelect}) => {
  const {data, isLoading} = useListBatchContactImports({pageSize: PAGE_SIZE}, {retry: false});

  const imports = data?.pages.flatMap((page) => page.imports) || [];

  return (
    <Autocomplete
      options={imports}
      loading={isLoading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.contactImportId === value.contactImportId}
      onChange={(_event, value) => {
        if (value) onSelect(value.contactImportId);
      }}
      disableClearable
      renderOption={(props, option) => (
        <li {...props} key={option.contactImportId}>
          <Stack spacing={1} sx={{paddingTop: 1, paddingBottom: 1, width: '100%'}}>
            <Typography variant={'body2'} fontWeight={'bold'}>
              {option.name}
            </Typography>
            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} width={'100%'}>
              <Typography variant={'caption'} color={'secondary'}>
                {toDate(option.createdAt)}
              </Typography>
              <Chip
                label={`${Number(option.totalCount).toLocaleString()} contacts`}
                color={'default'}
                size={'small'}
                variant={'outlined'}
                sx={{fontSize: 10}}
              />
            </Box>
          </Stack>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={'Select Import'}
          variant={'outlined'}
          sx={
            !params.inputProps.value
              ? {
                  '& label': {color: 'var(--mui-palette-primary-main)'},
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {borderColor: 'var(--mui-palette-primary-main)'},
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'var(--mui-palette-primary-main)',
                  },
                }
              : undefined
          }
        />
      )}
      sx={{minWidth: 340}}
      slotProps={{
        clearIndicator: {
          sx: {
            color: 'var(--mui-palette-primary-main)',
          },
        },
      }}
    />
  );
};

export default ImportsSelect;
